import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { TrendingUp, Package, Users, ShoppingBag, ArrowRight, Clock } from "lucide-react";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return <div className="p-10 font-script text-3xl text-blush-600">loading…</div>;
  }

  const k = data.kpis;
  const series = data.revenue_series || [];
  const max = Math.max(1, ...series.map((s) => s.revenue));

  const cards = [
    { label: "Revenue · 30d", value: fmt(k.total_revenue_30d), icon: TrendingUp, accent: "text-blush-600" },
    { label: "Orders · 30d", value: k.orders_30d, icon: ShoppingBag, accent: "text-ink-900" },
    { label: "Avg order value", value: fmt(k.avg_order_value_30d), icon: TrendingUp, accent: "text-ink-900" },
    { label: "Pending fulfilment", value: k.pending_fulfillment, icon: Clock, accent: "text-blush-600" },
    { label: "Active products", value: k.active_products, icon: Package, accent: "text-ink-900" },
    { label: "Total customers", value: k.total_customers, icon: Users, accent: "text-ink-900" },
  ];

  return (
    <div className="p-6 md:p-10" data-testid="admin-dashboard">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
        <div>
          <span className="font-script text-3xl text-blush-400">at a glance</span>
          <h1 className="font-heading text-3xl md:text-4xl text-ink-900">Dashboard</h1>
        </div>
        <Link to="/admin/orders" className="btn-ghost-rose inline-flex items-center gap-2">
          View all orders <ArrowRight size={14} strokeWidth={1.6} />
        </Link>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-10">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl bg-white border border-blush-100 p-5">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-body uppercase tracking-[0.2em] text-ink-500">
                {c.label}
              </p>
              <c.icon size={14} strokeWidth={1.6} className={c.accent} />
            </div>
            <p className={`mt-3 font-heading text-2xl ${c.accent}`}>{c.value}</p>
          </div>
        ))}
      </section>

      {/* Revenue chart */}
      <section className="rounded-3xl bg-white border border-blush-100 p-6 mb-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-2xl text-ink-900">Revenue · last 30 days</h2>
          <span className="text-xs font-body text-ink-500">INR</span>
        </div>
        <div className="flex items-end gap-1 h-44">
          {series.map((s, i) => (
            <div key={i} className="flex-1 flex flex-col items-stretch gap-1 group">
              <div
                className="bg-blush-200 group-hover:bg-blush-400 rounded-md transition-colors duration-300"
                style={{ height: `${(s.revenue / max) * 100}%`, minHeight: 2 }}
                title={`${s.date} · ${fmt(s.revenue)}`}
              />
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-between text-[10px] font-body text-ink-500">
          <span>{series[0]?.date}</span>
          <span>{series[series.length - 1]?.date}</span>
        </div>
      </section>

      {/* Recent orders */}
      <section className="rounded-3xl bg-white border border-blush-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-blush-100 flex items-center justify-between">
          <h2 className="font-heading text-xl md:text-2xl text-ink-900">Recent orders</h2>
          <Link to="/admin/orders" className="text-xs font-body uppercase tracking-[0.22em] text-blush-600 hover:text-blush-700">
            See all
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-body min-w-[640px]">
          <thead>
            <tr className="bg-blush-50/40 text-left text-[11px] uppercase tracking-[0.18em] text-ink-500">
              <th className="px-6 py-3">Order</th>
              <th className="px-6 py-3">Customer</th>
              <th className="px-6 py-3">Total</th>
              <th className="px-6 py-3">Payment</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {(data.recent_orders || []).map((o) => (
              <tr key={o.id} className="border-t border-blush-100 hover:bg-blush-50/40 transition">
                <td className="px-6 py-3">
                  <Link to={`/admin/orders/${o.id}`} className="text-blush-600 hover:underline">
                    {o.order_number}
                  </Link>
                </td>
                <td className="px-6 py-3 text-ink-900">{o.customer_name}</td>
                <td className="px-6 py-3 text-ink-900">{fmt(o.total)}</td>
                <td className="px-6 py-3">
                  <StatusBadge value={o.payment_status} />
                </td>
                <td className="px-6 py-3">
                  <StatusBadge value={o.fulfillment_status} subtle />
                </td>
                <td className="px-6 py-3 text-ink-500 text-xs">
                  {(o.created_at || "").slice(0, 10)}
                </td>
              </tr>
            ))}
            {(data.recent_orders || []).length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-ink-500 font-body">
                  No orders yet — your first sale will appear here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ value, subtle }) {
  const colours = {
    paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    failed: "bg-rose-50 text-rose-700 border-rose-200",
    refunded: "bg-slate-50 text-slate-700 border-slate-200",
    unfulfilled: "bg-blush-50 text-blush-700 border-blush-200",
    shipped: "bg-blue-50 text-blue-700 border-blue-200",
    delivered: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-slate-50 text-slate-700 border-slate-200",
  };
  const cls = colours[value] || "bg-blush-50 text-blush-700 border-blush-200";
  return (
    <span className={`inline-block rounded-full border px-3 py-0.5 text-[11px] uppercase tracking-[0.18em] ${cls}`}>
      {value || "—"}
    </span>
  );
}
